import { Switch, Route, useLocation } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { LanguageProvider } from "@/components/language-provider";
import { SettingsProvider } from "@/components/settings-provider";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import Pricing from "@/pages/pricing";
import Help from "@/pages/help";
import EnterpriseContact from "@/pages/enterprise-contact";
import Terms from "@/pages/terms";
import Privacy from "@/pages/privacy";
import License from "@/pages/license";
import Dynamics365 from "@/pages/dynamics365";
import ApiDocs from "@/pages/api-docs";
import { SiteAssistantWidget } from "@/components/assistant/assistant-widget";
import { APP_URL } from "@/lib/config";

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => { if (!window.location.hash) window.scrollTo(0, 0); }, [location]);
  return null;
}

/**
 * "Validar documento" opens the app's public verification page, the same one the QR code of a signed PDF opens
 * (drop the PDF or type the code). /validar had a link in the menu and the footer but no route, so it showed 404.
 */
function OpenVerification({ params }: { params: { code?: string } }) {
  useEffect(() => {
    const code = params.code ? `/${encodeURIComponent(params.code)}` : "";
    window.location.replace(`${APP_URL}/verificar${code}${window.location.search}`);
  }, [params.code]);
  return null;
}

function Router() {
  return (
    <>
    <ScrollToTop />
    <Switch>
      <Route path="/validar" component={OpenVerification} />
      <Route path="/validar/:code" component={OpenVerification} />
      <Route path="/verificar" component={OpenVerification} />
      <Route path="/verificar/:code" component={OpenVerification} />
      <Route path="/" component={Landing} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/help" component={Help} />
      <Route path="/enterprise-contact" component={EnterpriseContact} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/license" component={License} />
      <Route path="/dynamics365" component={Dynamics365} />
      <Route path="/api-docs" component={ApiDocs} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SettingsProvider>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
              <SiteAssistantWidget />
            </TooltipProvider>
          </LanguageProvider>
        </SettingsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
